import { FC, ReactNode } from "react";
import * as _ from "lodash";
import { useSelector } from "react-redux";

import { Card as AntDesignCard, RightOutlined, Tag, Typography, Grid } from "@ctra/components";
import { Enterprise, useTranslation } from "@ctra/i18n";

import { DataDescriptorEntity, Enterprise as App, EnterpriseAppState, SectionEntity } from "@ctra/api";

import { useFarm } from "@farms";
import { SectionVariant } from "../Stack";
import styles from "./Card.module.less";

interface CardProps {
  /**
   * "card of section"
   */
  of: SectionEntity;
  /**
   * render the chart component
   * @param componentID
   */
  renderChart?: (dataDescriptorID: DataDescriptorEntity["id"]) => ReactNode;
  /**
   * handle click
   */
  onClick?: () => void;
  /**
   * farm/overview variant of the card
   */
  variant?: SectionVariant;
}

const { Title, Paragraph, Link } = Typography;

/**
 * Layout card component with a main chart and description
 * @param id
 * @param title
 * @param isNew
 * @param children
 * @param renderChart
 * @constructor
 */
export const Card: FC<CardProps> = ({
  of: {
    id,
    title,
    flags: { isNew }
  },
  renderChart,
  onClick,
  variant = SectionVariant.overview
}) => {
  const { goto, markedNew, title: sectionTitle } = Enterprise.layouts.section;
  const { md } = Grid.useBreakpoint();
  const { farm } = useFarm();

  /**
   * Use the proper key for the farm or the dashboard sections
   */
  const {
    section: { description }
  } = Enterprise.layouts[variant === SectionVariant.overview ? "dashboard" : "farm"];

  const { t } = useTranslation();

  /**
   * Get a map of sections to unsupported data descriptor id's
   */
  const supportedChartsMap = useSelector<
    EnterpriseAppState,
    Record<SectionEntity["id"], Array<DataDescriptorEntity["id"]>>
  >((state) => (id ? App.entities.getSupportedCharts(state, { farmID: farm?.id }) : {}));

  /**
   * Get the first supported data descriptor ID
   */
  const dataDescriptorID = _.first(supportedChartsMap[id]);

  /**
   * Make a cover for the card when there is a component for it
   */
  const chart = dataDescriptorID && renderChart ? renderChart(dataDescriptorID) : null;

  return (
    <AntDesignCard
      className={styles.Card}
      title={
        md ? (
          <>
            <Title level={5}>
              {t<string>(sectionTitle(title), { variant, makeDefaultValue: true })}{" "}
              {isNew && (
                <Tag className={styles.Tag} color="#f50">
                  {t<string>(markedNew)}
                </Tag>
              )}
            </Title>
            <Paragraph type="secondary">{t<string>(description(title))}</Paragraph>
            <Link onClick={onClick}>
              {t<string>(goto)} <RightOutlined />
            </Link>
          </>
        ) : (
          <>
            <Link onClick={onClick}>
              {t<string>(sectionTitle(title), { variant, makeDefaultValue: true })}{" "}
              {isNew && (
                <Tag className={styles.Tag} color="#f50">
                  {t<string>(markedNew)}
                </Tag>
              )}{" "}
              <RightOutlined />
            </Link>
            <Paragraph type="secondary">{t<string>(description(title))}</Paragraph>
          </>
        )
      }
    >
      {md && chart}
    </AntDesignCard>
  );
};
